import { ReportHeader } from "views/applications-id-report/components/ReportHeader";
import { useAuthContext } from "providers/Auth";
import {
  ApplicantStatus,
  ApplicationStatus,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { Tabs } from "shared/components/BaseTabs";
import { getDisabledRerunTooltip } from "../utils";
import { useCombinedIncomeContext } from "../providers/useCombinedIncome";
import {
  INCOMPLETE_APPLICATION_STATUSES,
  INCOMPLETE_VERIFICATION_STATUSES
} from "../constants";

export const ReportHeaderSlice = () => {
  const {
    applicant,
    applicationId,
    applicantId,
    applicationStatus,
    applicationMeta,
    activeVerification,
    currentlySelectedVerificationId,
    setCurrentlySelectedVerificationId,
    activeVerificationId,
    activeCombinedIncomeVerification,
    selectedCombinedIncomeVerification,
    pendingReverifyRequests,
    isReadOnlyVerification: isReadOnly,
    verificationStatus,
    verificationTabs,
    isCancelingRerun,
    isRerunning,
    reportModals: { setShowRerunModal, setShowCancelRerunModal }
  } = useCombinedIncomeContext();
  const { permissions } = useAuthContext();

  const isVerificationIncomplete =
    INCOMPLETE_VERIFICATION_STATUSES.includes(verificationStatus) &&
    INCOMPLETE_APPLICATION_STATUSES.includes(
      applicationMeta.data?.status as ApplicationStatus
    );

  const activeVerificationStatus =
    activeCombinedIncomeVerification?.status ?? verificationStatus;
  const isActiveVerificationInFlight = (
    [
      VerificationStatus.Pending,
      VerificationStatus.Processing,
      VerificationStatus.Started,
      VerificationStatus.Submitted
    ] as VerificationStatus[]
  ).includes(activeVerificationStatus);
  const isActiveVerificationRerunInProgress =
    Boolean(activeCombinedIncomeVerification?.rerun_version) &&
    isActiveVerificationInFlight;

  const disabledRerunModalTooltip = getDisabledRerunTooltip({
    canEditIncome: permissions.canEditIncome,
    isReadOnly,
    isRerunInProgressOnActive: isActiveVerificationRerunInProgress,
    isApplicantSubmitted:
      applicant?.application_status === ApplicantStatus.Submitted,
    isViewingActive: currentlySelectedVerificationId === activeVerificationId,
    activeIsInFlight: isActiveVerificationInFlight,
    activeIsCanceled: activeVerificationStatus === VerificationStatus.Canceled,
    hasPendingReverify: pendingReverifyRequests.length > 0
  });

  const shouldShowCancelRerun =
    isActiveVerificationRerunInProgress && !isReadOnly;

  const reportDate = isVerificationIncomplete
    ? undefined
    : activeVerification.updated_at;

  return (
    <>
      <div className="-mt-2 mb-6 border-b-1 max-w-[90vw] md:max-w-[80vw] lg:max-w-[min(60vw,900px)] w-fit">
        {verificationTabs && verificationTabs.length > 1 && (
          <Tabs
            tabs={verificationTabs}
            active={currentlySelectedVerificationId!}
            onChange={setCurrentlySelectedVerificationId}
            size="sm"
            tabStyle="line"
          />
        )}
      </div>

      <ReportHeader
        applicationStatus={applicationStatus}
        applicationId={applicationId || "0"}
        title="Income history"
        applicantId={applicantId || 0}
        name={VerificationNames.CombinedIncome}
        reportDate={reportDate}
        hidePrint={isVerificationIncomplete}
        onRerunReport={
          shouldShowCancelRerun
            ? () => setShowCancelRerunModal(true)
            : () => setShowRerunModal(true)
        }
        rerunRequirements={
          selectedCombinedIncomeVerification?.rerun_requirements
        }
        disabledRerunModalTooltip={disabledRerunModalTooltip}
        reRunReportButtonText={
          shouldShowCancelRerun ? "Cancel income re-run" : "Re-run report"
        }
        reRunLoading={isRerunning || isCancelingRerun}
      />
    </>
  );
};
